/* Ranking uses current imagery, never identifiers, stock or dates. IDs only join native cards. */
(() => {
  const cache = new Map();
  const parser = new DOMParser();
  const words = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
  const singular = word => word.length < 4 ? word :
    word.endsWith('ies') ? word.slice(0, -3) + 'y' :
    /(?:ches|shes|xes|zes|sses)$/.test(word) ? word.slice(0, -2) :
    word.endsWith('s') && !/(?:ss|us|is)$/.test(word) ? word.slice(0, -1) : word;
  const tokens = value => words(value).map(singular);
  const expanded = list => list.flatMap(word => {
    const parts = word.match(/^(bed|pillow|table)(sheet|case|spread|cloth)$/);
    return parts ? [word, parts[1], parts[2]] : [word];
  });
  const phrase = list => list.flatMap(word => {
    const parts = word.match(/^(bed|pillow|table)(sheet|case|spread|cloth)$/);
    return parts ? [parts[1], parts[2]] : [word];
  });
  const containsAll = (field, query) => query.every(word => field.includes(word));
  const overlap = (field, query) => query.filter(word => field.includes(word)).length;
  // Small terminology families, not query-specific overrides or a product whitelist.
  const related = [['pillow', 'cushion'], ['duvet', 'comforter']];
  const near = (a, b) => {
    if (a.length >= 3 && b.startsWith(a)) return true;
    if (a.length < 4 || Math.abs(a.length - b.length) > 1) return false;
    let i = 0, j = 0, edits = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++edits > 1) return false;
      if (a.length >= b.length) i++;
      if (b.length >= a.length) j++;
    }
    return edits + (i < a.length || j < b.length ? 1 : 0) <= 1;
  };
  const relevance = (item, term) => {
    const data = item.search || {};
    const stop = new Set(['a', 'an', 'the', 'for', 'with', 'and', 'of', 'in', 'on']);
    const query = phrase(tokens(term)).filter(w => !stop.has(w));
    if (!query.length) return [10, 0];
    const title = tokens(data.title);
    const vendor = tokens(data.vendor);
    const withoutBrand = vendor.length && vendor.every((w, i) => title[i] === w)
      ? title.slice(vendor.length) : title;
    const type = phrase(tokens(data.type));
    const variants = (data.variants || []).map(value => phrase(tokens(value)));
    const titleWords = query.length > 1 ? expanded(title) : title;
    const titleCoverage = overlap(titleWords, query);
    if (phrase(title).join(' ') === query.join(' ') || phrase(withoutBrand).join(' ') === query.join(' ')) return [0, -query.length];
    if (containsAll(title, query) || (query.length > 1 && containsAll(expanded(title), query))) {
      const accessoryWords = ['case', 'cover'];
      const accessory = expanded(title).some(w => accessoryWords.includes(w)) &&
        !query.some(w => accessoryWords.includes(w));
      return [accessory ? 5 : 1, -query.length];
    }
    if (containsAll(type, query) || (query.length > 1 && containsAll(expanded(type), query))) return [2, -query.length];
    if (variants.some(v => containsAll(v, query))) return [3, -query.length];
    // For equal partial coverage, the final query word is usually the product noun:
    // a sheet match beats a generic "flat" match, independently of image quality.
    if (titleCoverage) return [4, -titleCoverage, titleWords.includes(query.at(-1)) ? -1 : 0];
    const closeTitle = expanded(title.concat(type));
    const relatedCoverage = query.filter(w => closeTitle.includes(w) || related.some(group =>
      group.includes(w) && group.some(other => closeTitle.includes(other)))).length;
    if (relatedCoverage) return [5, -relatedCoverage];
    const categories = tokens((data.categories || []).join(' '));
    if (containsAll(categories, query)) return [6, -query.length];
    const tags = tokens((data.tags || []).join(' '));
    if (containsAll(tags, query)) return [7, -query.length];
    if (containsAll(tokens(data.body), query)) return [8, -query.length];
    const fuzzy = query.filter(w => title.concat(type).some(t => near(w, t))).length;
    return fuzzy ? [9, -fuzzy] : [10, 0];
  };
  const sort = (items, search, term = '') => {
    const keyed = items.map(item => ({ item, key: search ? relevance(item, term) : [0, 0] }));
    keyed.sort((a, b) => a.key[0] - b.key[0] || a.key[1] - b.key[1] || (a.key[2] || 0) - (b.key[2] || 0) ||
      a.item.rank - b.item.rank || a.item.index - b.item.index);
    return keyed.map(entry => entry.item);
  };
  const enabled = (mode, url) => {
    const order = url.searchParams.get('sort_by');
    return mode === 'search' ? !order || order === 'relevance' : !order || order === 'manual';
  };
  async function html(url, signal) {
    const response = await fetch(url, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Ranking response failed');
    return parser.parseFromString(await response.text(), 'text/html');
  }
  async function index(url, signal) {
    const base = new URL(url);
    base.searchParams.delete('page');
    base.searchParams.delete('sections');
    base.searchParams.set('section_id', 'inhome-quality-index');
    const key = base.href;
    if (cache.has(key)) return cache.get(key);
    const read = async page => {
      const u = new URL(base); u.searchParams.set('page', page);
      const doc = await html(u, signal);
      const node = doc.querySelector('[data-inhome-quality-index]');
      if (!node) throw new Error('Missing ranking index');
      const data = JSON.parse(node.textContent);
      if (data.page !== page || data.pages > 100) throw new Error('Incomplete ranking index');
      return data;
    };
    const first = await read(1);
    const chunks = [first];
    // Lightweight metadata only; fetch subsequent index pages sequentially, not every product card.
    for (let page = 2; page <= first.pages; page++) chunks.push(await read(page));
    if (chunks.some(c => c.total !== first.total)) throw new Error('Catalogue changed during ranking');
    const raw = chunks.flatMap(c => c.items);
    if (raw.length !== first.total) throw new Error('Incomplete catalogue');
    const items = raw.filter(Boolean);
    if (new Set(items.map(i => i.id)).size !== items.length) throw new Error('Duplicate catalogue result');
    cache.set(key, items);
    return items;
  }
  async function rankGrid(root) {
    const context = root.querySelector('[data-ih-ranking-context]');
    if (!context) return;
    const key = location.href + '|' + context.dataset.mode + '|' + context.dataset.page;
    if (root.dataset.ihRankingKey === key && root.dataset.ihRankingState) return;
    root.dataset.ihRankingKey = key;
    delete root.dataset.ihRankingState;
    root.dataset.ihRankingMode = context.dataset.mode;
    root.dataset.ihPageSize = context.dataset.size;
    root.dataset.ihPage = context.dataset.page;
    const url = new URL(location.href);
    const mode = root.dataset.ihRankingMode;
    if (!enabled(mode, url)) { root.dataset.ihRankingState = 'explicit-sort'; return; }
    const grid = root.querySelector('ul.product-grid');
    if (!grid) return;
    root.dataset.ihRankingState = 'loading';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const items = sort(await index(url, controller.signal), mode === 'search', url.searchParams.get('q'));
      if (!root.isConnected || new URL(location.href).href !== url.href) return;
      const size = Number(root.dataset.ihPageSize);
      const page = Number(root.dataset.ihPage);
      const wanted = items.slice((page - 1) * size, page * size);
      if (!wanted.length) return;
      const cards = new Map([...grid.querySelectorAll(':scope > li[data-product-id]')].map(n => [n.dataset.productId, n]));
      const pages = [...new Set(wanted.filter(i => !cards.has(i.id)).map(i => Math.floor(i.index / size) + 1))];
      // Fetch only native pages holding the chosen cards. Retain native card markup and all product controls.
      for (const nativePage of pages) {
        const u = new URL(url);
        u.searchParams.set('section_id', root.getAttribute('section-id'));
        u.searchParams.set('page', nativePage);
        const doc = await html(u, controller.signal);
        for (const card of doc.querySelectorAll('ul.product-grid > li[data-product-id]')) cards.set(card.dataset.productId, card);
      }
      if (wanted.some(i => !cards.has(i.id))) throw new Error('Missing native product card');
      if (!root.isConnected || location.href !== url.href) return;
      const fragment = document.createDocumentFragment();
      wanted.forEach((item, i) => {
        const node = cards.get(item.id);
        node.dataset.page = String(page);
        node.className = node.className.replace(/product-grid__item--\d+/g, `product-grid__item--${i}`);
        node.dataset.ihVisualRank = String(item.rank);
        if (mode === 'search') node.dataset.ihRelevanceTier = String(relevance(item, url.searchParams.get('q'))[0]);
        fragment.append(node);
      });
      grid.replaceChildren(fragment);
      root.dataset.ihRankingState = 'complete';
      root.dataset.ihRankingTotal = String(items.length);
    } catch (error) {
      // All-or-nothing: native results and links remain usable on timeout/network/index failure.
      root.dataset.ihRankingState = 'native-fallback';
      console.warn('INHOME image ranking kept native results:', error.message);
    } finally { clearTimeout(timeout); }
  }
  let queued = false;
  const rankPredictive = list => {
    const nodes = [...list.querySelectorAll(':scope > li[data-ih-search-product]')];
    if (!nodes.length) return;
    const query = list.dataset.ihSearchQuery;
    const items = nodes.map((node, index) => ({
      node, index, rank: Number(node.dataset.ihVisualRank), search: JSON.parse(node.dataset.ihSearchProduct)
    }));
    const ordered = sort(items, true, query);
    if (ordered.some((item, i) => item.node !== nodes[i])) {
      list.replaceChildren(...ordered.map(item => item.node));
    }
    list.dataset.ihRelevanceState = 'complete';
  };
  const scan = () => {
    queued = false;
    document.querySelectorAll('results-list').forEach(rankGrid);
    document.querySelectorAll('[data-ih-search-query]').forEach(rankPredictive);
  };
  const observer = new MutationObserver(records => {
    if (!queued && records.some(r => r.type === 'childList')) {
      queued = true; requestAnimationFrame(scan);
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  scan();
})();
